import {
  StoresDocument,
  StoresQuery,
  Where,
  WhereOp,
} from '@/types/generated/graphql'
import { setWhereValue } from '@/utils/searchConditionDef'

/**
 * 発注予算系 GraphQL where: 店舗エリア
 * store/vUiPAreas は履歴行を含む secondary 結合のため絞り込みが不安定。
 * 業務日時点で有効な store/validVUiPAreas を使う。
 */
export const BUDGET_CONF_STORE_AREA_WHERE_FIELD =
  'store/validVUiPAreas/areaCd'

/** 店舗マスタ側のエリア条件（店舗一覧・エリア→店舗解決） */
export const STORE_AREA_WHERE_FIELD = 'validVUiPAreas/areaCd'

/** 発注予算設定の出力から除外する店舗コード範囲（数値比較） */
export const BUDGET_CONF_EXCLUDED_STORE_CD_MIN = 900
export const BUDGET_CONF_EXCLUDED_STORE_CD_MAX = 999

export type BudgetStoreExtractCondition = {
  storeShelve: string[]
  store: string[]
}

type StoreEdges = NonNullable<
  NonNullable<StoresQuery['stores']>['edges']
>

type ApolloQueryClient = {
  query: (options: {
    query: typeof StoresDocument
    variables: {
      where: Where[]
      first: number
      after?: string | null
    }
    fetchPolicy?: string
  }) => Promise<{ data: StoresQuery }>
}

const STORES_PAGE_SIZE = 1000

/**
 * 店舗コードが 900〜999（ゼロ埋め含む）なら出力除外対象。
 */
export function isBudgetConfExcludedStoreCd(storeCd: string): boolean {
  const n = Number.parseInt(String(storeCd).trim(), 10)
  return (
    Number.isFinite(n) &&
    n >= BUDGET_CONF_EXCLUDED_STORE_CD_MIN &&
    n <= BUDGET_CONF_EXCLUDED_STORE_CD_MAX
  )
}

export function filterOutBudgetConfExcludedStoreCds(
  storeCds: string[],
): string[] {
  return storeCds.filter((cd) => !isBudgetConfExcludedStoreCd(cd))
}

/**
 * GraphQL storeCd NOT_IN 用。ゼロ埋め（3〜5桁）の表記ゆれを含める。
 */
export function budgetConfExcludedStoreCdList(): string[] {
  const list: string[] = []
  for (
    let i = BUDGET_CONF_EXCLUDED_STORE_CD_MIN;
    i <= BUDGET_CONF_EXCLUDED_STORE_CD_MAX;
    i += 1
  ) {
    const raw = String(i)
    list.push(raw, raw.padStart(4, '0'), raw.padStart(5, '0'))
  }
  return list
}

/** 発注予算設定の where に店舗除外条件を付与する */
export function appendBudgetConfExcludedStoreCdWhere(
  wheres: Where[],
): Where[] {
  wheres.push({
    field: ['storeCd'],
    op: WhereOp.NotIn,
    value: budgetConfExcludedStoreCdList(),
  })
  return wheres
}

export async function fetchStoreCdsByArea(
  apollo: ApolloQueryClient,
  areaCds: string[],
): Promise<string[]> {
  const areaCdList = [...areaCds]
  if (!areaCdList.length) {
    return []
  }

  const where: Where[] = [
    {
      field: [STORE_AREA_WHERE_FIELD],
      op: WhereOp.In,
      value: [''],
    },
  ]
  setWhereValue(where, STORE_AREA_WHERE_FIELD, areaCdList)

  const storeCds: string[] = []
  let after: string | null = null
  let hasMore = true
  while (hasMore) {
    const result: { data: StoresQuery } = await apollo.query({
      query: StoresDocument,
      variables: {
        where,
        first: STORES_PAGE_SIZE,
        after,
      },
      fetchPolicy: 'network-only',
    })
    const edges: StoreEdges = result.data.stores?.edges ?? []
    for (const edge of edges) {
      const storeCd = edge?.node?.storeCd
      if (storeCd) {
        storeCds.push(storeCd)
      }
    }
    if (edges.length < STORES_PAGE_SIZE) {
      hasMore = false
      continue
    }
    const lastCursor: string | null | undefined =
      edges[edges.length - 1]?.cursor
    if (!lastCursor) {
      hasMore = false
      continue
    }
    after = lastCursor
  }

  return [...new Set(storeCds)]
}

/**
 * 予算作成パイプライン用の店舗コード一覧を解決する。
 * - 店舗エリアのみ: エリア内の全店舗
 * - 店舗のみ: 指定店舗
 * - 両方: 積集合（AND）
 *
 * 呼び出し側の配列が await 中に書き換わっても影響を受けないよう、入力はコピーして扱う。
 */
export async function resolveStoreCdsForBudget(
  apollo: ApolloQueryClient,
  extractCondition: BudgetStoreExtractCondition,
): Promise<string[]> {
  const storeShelve = [...(extractCondition.storeShelve ?? [])]
  const explicitStores = [...(extractCondition.store ?? [])]
  const areaStores = await fetchStoreCdsByArea(apollo, storeShelve)

  if (explicitStores.length > 0 && storeShelve.length > 0) {
    const areaSet = new Set(areaStores)
    return explicitStores.filter((storeCd) => areaSet.has(storeCd))
  }
  if (explicitStores.length > 0) {
    return explicitStores
  }
  return areaStores
}
